import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Card, CardContent } from '../../components/ui/Card';
import RegularizationStats from './components/RegularizationStats';
import BatchCard from './components/BatchCard';
import VendorSelectionModal from './components/VendorSelectionModal';
import TablePagination from '../vendor-master-list/components/TablePagination';
import { useToast } from '../../components/ui/Toast';
import { regularizationService, getErrorMessage } from '../../services/apiService';

const VendorRegularization = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const [stats, setStats] = useState({
    pendingBatches: 0,
    completedBatches: 0,
    expiredRequests: 0,
  });
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    search: '',
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchStats();
    fetchBatches();
  }, [isAdmin, navigate, pagination.page, filters.status, filters.search]);

  const fetchStats = async () => {
    try {
      const response = await regularizationService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await regularizationService.getBatches(
        pagination.page,
        pagination.limit,
        filters.status,
        filters.search
      );
      if (response.success) {
        setBatches(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.meta?.total || 0,
          totalPages: response.meta?.totalPages || 0,
        }));
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to fetch batches');
    } finally {
      setLoading(false);
    }
  };

  const handleSendBulk = async (vendorIds) => {
    try {
      const response = await regularizationService.sendBulk(vendorIds);
      if (response.success) {
        toast.success(response.message || `Regularization links sent to ${vendorIds.length} vendors`);
        setShowVendorModal(false);
        fetchStats();
        fetchBatches();
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to send regularization links');
    }
  };

  const handleViewBatch = (batchId) => {
    navigate(`/vendor-regularization/batch/${batchId}`);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-6">
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendor Regularization', path: '/vendor-regularization' },
        ]}
      />

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Vendor Regularization</h1>
            <p className="text-gray-600 mt-1">
              Send bulk rectification links to vendors for periodic information updates
            </p>
          </div>
          <Button
            onClick={() => setShowVendorModal(true)}
            className="flex items-center gap-2"
          >
            <Icon name="Send" size={18} />
            Send Bulk Rectification Link
          </Button>
        </div>
      </div>

      <RegularizationStats stats={stats} />

      <div className="mt-6">
        <div className="mb-4 flex items-center gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search batches..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="COMPLETED">Completed</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : batches.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Icon name="Inbox" size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No batches found</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {batches.map((batch) => (
                <BatchCard
                  key={batch.id}
                  batch={batch}
                  onViewDetails={() => handleViewBatch(batch.id)}
                />
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-6">
                <TablePagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {showVendorModal && (
        <VendorSelectionModal
          onClose={() => setShowVendorModal(false)}
          onConfirm={handleSendBulk}
        />
      )}
    </div>
  );
};

export default VendorRegularization;
